import { z } from 'zod';

/**
 * 金额统一使用“微元”（1e-6 币种单位）整数表示，避免浮点误差。
 * 对外序列化为固定 6 位小数的字符串，例如 "5.000000"。
 */
export const MICROS_PER_UNIT = 1_000_000n;

export type Currency = 'CNY' | 'USD';
export const CurrencySchema = z.enum(['CNY', 'USD']);

const MONEY_RE = /^-?\d{1,12}(\.\d{1,6})?$/;

export const MoneyStringSchema = z
  .string()
  .regex(MONEY_RE, '金额格式应为最多 6 位小数的十进制字符串，例如 "5.000000"');

export function microsToString(micros: bigint): string {
  const negative = micros < 0n;
  const abs = negative ? -micros : micros;
  const whole = abs / MICROS_PER_UNIT;
  const frac = (abs % MICROS_PER_UNIT).toString().padStart(6, '0');
  return `${negative ? '-' : ''}${whole}.${frac}`;
}

export function parseMoney(value: string): bigint {
  if (!MONEY_RE.test(value)) throw new Error(`invalid money: ${value}`);
  const negative = value.startsWith('-');
  const body = negative ? value.slice(1) : value;
  const [whole = '0', frac = ''] = body.split('.');
  const micros = BigInt(whole) * MICROS_PER_UNIT + BigInt(frac.padEnd(6, '0'));
  return negative ? -micros : micros;
}

/** 显示用：保留 2 位小数（四舍五入），精确值放在 title 中展示。 */
export function formatMoneyDisplay(value: string, currency: Currency, locale = 'zh-CN'): string {
  const micros = parseMoney(value);
  const cents = (micros + (micros >= 0n ? 5_000n : -5_000n)) / 10_000n;
  const asNumber = Number(cents) / 100;
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 }).format(
    asNumber,
  );
}

export function currencySymbol(currency: Currency): string {
  return currency === 'CNY' ? '¥' : '$';
}
